import type {
  ApolloClient,
  ApolloError,
  NormalizedCacheObject,
} from "@apollo/client";
import React, { createContext, useContext, useEffect } from "react";
import { useStores } from "../stores";
import { observer } from "mobx-react";
import { BootShell } from "layouts/bootShell/BootShell";
import { useUser } from "api/hooks/useUser";
import { API_ERROR_CODES } from "api/constants";
import { isUnauthorized } from "api/apolloErrorUtils";
import { ErrorPage } from "pages/ErrorPage";
import { parseUiState } from "api/uiState";
import {
  LOCAL_STORAGE_USER,
  STORAGE_TYPE,
  storeDirectlyIntoStorage,
  UI_STATE,
} from "utils/localStorage";
import { useSessionBootstrap } from "./useSessionBootstrap";
import { useSentryUser } from "./useSentryUser";

type AuthState = {
  isAuthenticated: boolean;
};

export type RouterContext = {
  auth: AuthState;
  // Apollo client injected at router-creation time so `beforeLoad`/`loader`
  // functions can read GraphQL data via `context.client` instead of importing
  // the module singleton (keeps them testable with a mock client).
  client: ApolloClient<NormalizedCacheObject>;
};

const AuthContext = createContext<AuthState | undefined>(undefined);

/**
 * Decides sign-in from the `GET_USER` answer. The session cookie is
 * `HttpOnly`, so only the server knows if the session is active. Without a
 * session, `GET_USER` fails, and the visitor is signed out.
 */
export const AuthProvider = observer(function AuthProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { authStore, userStore } = useStores();
  const { user: cachedUser, loading, error } = useUser();
  // After a 401, Apollo can still return the user of the session that ended.
  // The 401 is the server's answer, so the visitor is signed out.
  const signedOut = isSignedOut(error);
  const user = signedOut ? undefined : cachedUser;
  // A login in progress is not a session yet.
  const { inProgress } = authStore;
  const isAuthenticated = !!user && !inProgress;

  // The stores keep a copy of the user until `authStore` is deleted.
  useEffect(() => {
    if (!user) {
      // Clear the user id that localStorage restored. Its readers must not
      // see a session that the server did not confirm.
      if (!loading) userStore.forgetUser();
      return;
    }
    userStore.updateId(user.id);
    userStore.setUser(user);
    // The router reads this copy before `GET_USER` is in the cache.
    storeDirectlyIntoStorage(
      UI_STATE,
      parseUiState(user.uiState),
      LOCAL_STORAGE_USER,
      STORAGE_TYPE.LOCAL,
    );
  }, [user, loading, userStore]);

  useSessionBootstrap(user);
  useSentryUser(user);

  if (loading && !user) {
    return <BootShell />;
  }

  // Do not send the user to login when the server did not answer the
  // question. A reload asks it again. After a GraphQL error, the errorLink
  // opens the error page of a failed IDP login. After a network error, it
  // does not.
  if (
    !user &&
    isSignInUnknown(error) &&
    (!!error?.networkError || !IDP_PATHS.includes(window.location.pathname))
  ) {
    return <ErrorPage />;
  }

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
});

const IDP_PATHS = ["/idp", "/idpError"];

// True when `GET_USER` failed because the session is missing or expired.
const isSignedOut = (error: ApolloError | undefined) =>
  !!error?.graphQLErrors.some(isUnauthorized);

/**
 * True when `GET_USER` failed for a reason other than a missing or expired
 * session: a network error, or a server error. A 401 means signed out. When
 * FusionAuth is down, the errorLink opens the outage page, so the router must
 * render.
 */
const isSignInUnknown = (error: ApolloError | undefined) =>
  !!error &&
  (!!error.networkError ||
    error.graphQLErrors.some(
      (graphQLError) =>
        !isUnauthorized(graphQLError) &&
        graphQLError.extensions?.code !== API_ERROR_CODES.FUSIONAUTH_DOWN_CODE,
    ));

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
